import { criterion, fromCriteria, unsupported } from "@/core/check";
import type { Lesson } from "@/core/types";
import { hasField, readFields, readLayout } from "@/office/word/layout";

export const lektionSeitenzahlen: Lesson = {
  id: "seitenzahlen",
  title: "Seitenzahlen als Feld einfügen",
  summary: "Warum Seitenzahlen niemals getippt werden.",
  durationMinutes: 7,
  learningGoals: [
    "Seitenzahlen über Einfügen → Seitenzahl ergänzen",
    "Felder als selbst berechnende Platzhalter verstehen",
    "Die Gesamtseitenzahl ergänzen",
  ],
  steps: [
    {
      id: "seitenzahl-einfuegen",
      title: "Die Seitenzahl einfügen",
      explanation: [
        "Eine Seitenzahl kann man nicht sinnvoll tippen: In der Fußzeile stünde auf jeder " +
          "Seite dieselbe Zahl, weil sich der Inhalt ja wiederholt.",
        "Word löst das mit einem Feld. Ein Feld ist ein Platzhalter, den Word selbst " +
          "ausrechnet – auf Seite 1 zeigt er 1, auf Seite 7 eine 7. Dieselbe Technik " +
          "steckt hinter dem Inhaltsverzeichnis aus dem Formatvorlagen-Modul.",
        "Du fügst sie über „Einfügen“ → „Seitenzahl“ ein und wählst die Position. Word " +
          "öffnet dabei automatisch die Fußzeile.",
      ],
      keyPoints: [
        "Einfügen → Seitenzahl → Seitenende.",
        "Ein Feld berechnet sich selbst und wird nie von Hand getippt.",
        "Mit Alt + F9 lassen sich die Feldfunktionen sichtbar machen.",
      ],
      task: {
        instruction: "Füge in die Fußzeile deines Dokuments eine automatische Seitenzahl ein.",
        hint: "Einfügen → Seitenzahl → „Seitenende“ → eine der einfachen Varianten wählen.",
        check: async () => {
          const felder = await readFields();
          if (felder === null) {
            const layout = await readLayout();
            return unsupported(
              "Diese Word-Version stellt dem Add-in keine Felder zur Verfügung, deshalb lässt " +
                `sich die Seitenzahl hier nicht prüfen. ${
                  layout.hasFooterText
                    ? "Eine Fußzeile mit Inhalt ist aber vorhanden."
                    : "Die Fußzeile ist noch leer."
                }`,
              "Prüfe selbst, ob auf Seite 2 eine andere Zahl steht als auf Seite 1. Danach kannst du den Schritt als erledigt markieren.",
            );
          }

          return fromCriteria(
            [criterion("Ein Seitenzahl-Feld ist vorhanden", hasField(felder, "Page"))],
            {
              success: "Die Seitenzahl zählt jetzt automatisch mit.",
              failure: "Es wurde noch kein Seitenzahl-Feld gefunden.",
              hint: "Eine getippte Zahl zählt nicht. Nutze wirklich Einfügen → Seitenzahl.",
            },
          );
        },
      },
    },
    {
      id: "seite-x-von-y",
      title: "„Seite X von Y“",
      explanation: [
        "Die verbreitete Angabe „Seite 3 von 12“ besteht aus zwei Feldern: der aktuellen " +
          "Seitenzahl und der Gesamtzahl der Seiten. Beide rechnet Word selbst aus.",
        "Am schnellsten geht es über „Einfügen“ → „Seitenzahl“ → „Seitenende“, wo unter " +
          "„Seite X von Y“ bereits fertige Varianten liegen. Wer es von Hand baut, fügt " +
          "das zweite Feld über „Einfügen“ → „Schnellbausteine“ → „Feld“ ein und wählt " +
          "dort „NumPages“.",
        "Das ist besonders bei ausgedruckten Dokumenten wertvoll: Wer den Stapel in die " +
          "Hand bekommt, sieht sofort, ob eine Seite fehlt.",
      ],
      keyPoints: [
        "PAGE ist die aktuelle Seite, NUMPAGES die Gesamtzahl.",
        "Fertige Varianten: Einfügen → Seitenzahl → Seitenende → „Seite X von Y“.",
        "F9 aktualisiert Felder, falls eine Angabe veraltet erscheint.",
      ],
      task: {
        instruction:
          "Ergänze deine Fußzeile so, dass sie sowohl die aktuelle Seite als auch die " +
          "Gesamtzahl der Seiten anzeigt.",
        hint: "Am einfachsten über die fertige Variante „Seite X von Y“.",
        check: async () => {
          const felder = await readFields();
          if (felder === null) {
            return unsupported(
              "Diese Word-Version stellt dem Add-in keine Felder zur Verfügung, deshalb lässt " +
                "sich diese Aufgabe nicht automatisch prüfen.",
              "Kontrolliere selbst, ob in der Fußzeile beide Zahlen stehen, und markiere den Schritt dann als erledigt.",
            );
          }

          return fromCriteria(
            [
              criterion("Feld für die aktuelle Seite (PAGE)", hasField(felder, "Page")),
              criterion("Feld für die Gesamtseitenzahl (NUMPAGES)", hasField(felder, "NumPages")),
            ],
            {
              success: "„Seite X von Y“ steht – beide Zahlen pflegt Word ab jetzt selbst.",
              failure: "Es fehlt noch eines der beiden Felder.",
              hint: "Die Gesamtseitenzahl ist ein eigenes Feld. Eine getippte Zahl dahinter erkennt Word nicht als solches.",
            },
          );
        },
      },
    },
  ],
};
